import { create } from "zustand";
import { persist } from "zustand/middleware";
import { api } from "@/api/client";

export interface User {
  id: string;
  email: string;
  name: string;
  role: "admin" | "editor" | "marketing" | "readonly";
}

interface AuthState {
  user: User | null;
  isLoading: boolean;
  error: string | null;

  // Actions
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  clearError: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isLoading: true,
      error: null,

      login: async (email: string, password: string) => {
        set({ isLoading: true, error: null });
        try {
          const response = await api.post<{ data: { user: User } }>(
            "/api/admin/auth/login",
            { email, password }
          );
          set({ user: response.data.user, isLoading: false });
          return true;
        } catch (error: unknown) {
          const message =
            error instanceof Error ? error.message : "Přihlášení se nezdařilo";
          set({ error: message, isLoading: false });
          return false;
        }
      },

      logout: async () => {
        try {
          await api.post("/api/admin/auth/logout", {});
        } catch {
          // Ignore errors during logout
        }
        set({ user: null });
      },

      checkAuth: async () => {
        // If we already have a user and we're not loading, skip the check
        if (get().user && !get().isLoading) {
          return;
        }

        set({ isLoading: true });
        try {
          const response = await api.get<{ data: { user: User } }>(
            "/api/admin/auth/me"
          );
          set({ user: response.data.user, isLoading: false });
        } catch {
          set({ user: null, isLoading: false });
        }
      },

      clearError: () => set({ error: null }),
    }),
    {
      name: "ckgo2-auth",
      partialize: (state) => ({ user: state.user }),
    }
  )
);
